import { useEffect, useState } from 'react';

import { isApp } from '@/lib/app';

/** What the site publishes about the Mac app: the version of the build on offer and the oldest macOS it runs on. */
export interface MacRelease {
  /** A plain dotted number: `0.31.19`. */
  readonly version: string;
  /** The oldest macOS, as the name of a release: `13`, `14.2`. Null when the appcast does not say. */
  readonly os: string | null;
}

const APPCAST_URL = '/download/appcast.xml';
const SPARKLE_NS = 'http://www.andymatuschak.org/xml-namespaces/sparkle';
const DOTTED = /^\d+(?:\.\d+){1,3}$/;

/** `13.0` reads as `13`, `14.2` stays. Anything that is not a dotted number is not shown. */
function osName(value: string | null): string | null {
  if (value === null || !/^\d+(?:\.\d+){0,2}$/.test(value)) return null;
  return value.replace(/(?:\.0)+$/, '');
}

/** The newest build in an appcast: its first item. Null when there is no item or its version is not a plain number. */
export function parseAppcast(xml: string): MacRelease | null {
  try {
    const document = new DOMParser().parseFromString(xml, 'application/xml');
    if (document.getElementsByTagName('parsererror').length > 0) return null;
    const item = document.getElementsByTagName('item')[0];
    if (item === undefined) return null;
    const text = (name: string): string | null => item.getElementsByTagNameNS(SPARKLE_NS, name).item(0)?.textContent.trim() ?? null;
    const version = text('shortVersionString');
    if (version === null || !DOTTED.test(version)) return null;
    return { version, os: osName(text('minimumSystemVersion')) };
  } catch {
    return null;
  }
}

let published: MacRelease | null = null;

/**
 * The Mac app's published version, from the appcast the site serves next to the download (the same file the app's updater
 * reads), asked once per visit and kept in memory. Null until it arrives, and whenever it cannot be had (offline, no
 * appcast, an odd answer): the caller then shows what it showed before, and the download is never held up. Not asked inside
 * the app itself, which has its own version.
 */
export function useMacRelease(): MacRelease | null {
  const [release, setRelease] = useState<MacRelease | null>(published);
  useEffect(() => {
    if (published !== null || isApp()) return undefined;
    const alive = { current: true };
    void (async () => {
      try {
        const response = await fetch(APPCAST_URL, { cache: 'no-cache' });
        if (!response.ok) return;
        const parsed = parseAppcast(await response.text());
        if (parsed === null) return;
        published = parsed;
        if (alive.current) setRelease(parsed);
      } catch {
        // offline or blocked: the line stays as it was
      }
    })();
    return () => {
      alive.current = false;
    };
  }, []);
  return release;
}

/** Forgets what was fetched (tests only). */
export function forgetMacRelease(): void {
  published = null;
}
